import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { AppDispatch } from '../../../store/widget/widget';
import { useDispatch } from 'react-redux';
import { setAppModule, ApplicationModule } from '../../../store/slices/common-slice';
import SDKMessenger from '../../../utils/services/sdk';
import { TrackingService } from '../../../utils/services/tracking';
import { useLocation } from 'react-router-dom';
import { BreakpointState, getApplicationModule, windowResizeHandler } from '../../../utils';
import { resetSDKParams } from '../../../store/slices/sdk-slice';
import useDebounce from '../../../hooks/use-debounce';

export const ConsumerWindowListeners: React.FC = (): React.ReactElement => {
    const application = getApplicationModule();
    const dispatch = useDispatch<AppDispatch>();
    const sdkMessenger = SDKMessenger.getSDKMessengerService();
    const trackingService = TrackingService.getTrackingService();
    const location = useLocation();

    const locationRef = useRef<string>(null);
    const breakpointsRef = useRef<BreakpointState>({
        isTabletPortrait: null,
        isMobilePortrait: null,
        isMobileLandscape: null,
        isSmallIframe: null,
    });

    const determineHistoryOnBack = async () => {
        if (location.key) {
            await trackingService.trackBrowserBackClick(`${location.pathname}`, false);
            return true;
        } else {
            dispatch(resetSDKParams());
            await trackingService.trackBrowserBackClick(`${location.pathname}`, true);
            sdkMessenger.closePrizeout();
        }
    };

    const resizeHandler = () => {
        breakpointsRef.current = windowResizeHandler(dispatch, breakpointsRef.current);
    };

    const [debouncedResize, cancelDebounce] = useDebounce(resizeHandler, 50);

    useEffect(() => {
        if (locationRef.current !== null && location.key !== locationRef.current) {
            determineHistoryOnBack();
        }

        locationRef.current = location.key;
    }, [location.key]);

    useLayoutEffect(() => {
        const preventTouchMove = (e: TouchEvent) => e.preventDefault();

        resizeHandler();
        dispatch(setAppModule(application as ApplicationModule));

        window.addEventListener('resize', debouncedResize);
        window.addEventListener('touchmove', preventTouchMove);

        return () => {
            window.removeEventListener('resize', debouncedResize);
            window.removeEventListener('touchmove', preventTouchMove);
            cancelDebounce();
        };
    }, [debouncedResize, cancelDebounce]);

    return null;
};
